import { useState, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { datadogLogs } from '@datadog/browser-logs';
import 'amazon-connect-streams';
import { DraftEmailContact, EmailClient } from '@amazon-connect/email';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { sendEmail } from '../../../../../../services/sendEmail';
import { fetchEmailBody } from '../../../../../../services/fetchEmailBody';
import { EmailAttachment, EmailMessage, SendEmailRequest } from '../../../../../../models/Email';
import { resolveEmailAttachments } from '../../../../../../services/resolveEmailAttachments';
import { Attachment } from '../../../../../../models/Attachment';
import { $generateHtmlFromNodes } from '@lexical/html';
import { OutboundEmailFields } from '../../Outbound/EmailOutboundHeader';
import config from '../../../../../../config';

interface UseSendEmailProps {
  onDiscard: () => void;
  onSuccess: () => void;
  onError: () => void;
  outboundFields?: OutboundEmailFields;
  attachments?: Attachment[];
}

interface UseSendEmailReturn {
  handleSend: () => Promise<void>;
  isLoading: boolean;
}

export function useSendEmail({
  onDiscard,
  onSuccess,
  onError,
  outboundFields,
  attachments = []
}: UseSendEmailProps): UseSendEmailReturn {
  const [editor] = useLexicalComposerContext();
  const { selectedEmail, emailData, appendMessageToThread, templateHtml } = useEmailContext();
  const [isLoading, setIsLoading] = useState(false);
  const agentUserIdRef = useRef<string>('');
  const emailClientRef = useRef<EmailClient | null>(null);

  useEffect(() => {
    connect.agent(agent => {
      const arn = agent.getAgentARN();
      const agentId = arn.split('/').pop() ?? arn;
      agentUserIdRef.current = agentId;
      emailClientRef.current = new EmailClient(connect.core.getSDKClientConfig() as any);
    });
  }, []);

  const handleSend = async (): Promise<void> => {
    if (!selectedEmail) return;

    // selectedEmail in the context may change during the sendMail
    const capturedContactId = selectedEmail;
    const isOutbound = !!outboundFields;

    let bodyHtml = '';

    editor.read(() => {
      const editorHtml = $generateHtmlFromNodes(editor);
      bodyHtml = templateHtml ? `${editorHtml}${templateHtml}` : editorHtml;
    });

    setIsLoading(true);

    try {
      validateAttachmentsUploaded();

      let sent: boolean;
      if (isOutbound) {
        sent = await handleSendOutbound(capturedContactId, bodyHtml);
      } else {
        sent = await handleSendReply(capturedContactId, bodyHtml);
      }

      if (sent) {
        onSuccess();
        onDiscard();
      }
    } catch (error) {
      datadogLogs.logger.error('Failed to send email', { channel: 'email' }, error as Error);
      onError();
    } finally {
      setIsLoading(false);
    }
  };

  const validateAttachmentsUploaded = (): void => {
    const missingUpload = attachments.find(attachment => !attachment.fileId);

    if (missingUpload) {
      throw new Error(`Attachment "${missingUpload.name}" is not uploaded yet`);
    }
  };

  const handleSendOutbound = async (contactId: string, bodyHtml: string): Promise<boolean> => {
    const client = emailClientRef.current;

    if (!client || !outboundFields) {
      datadogLogs.logger.error('Outbound email send skipped: client or fields not available', { channel: 'email' });
      return false;
    }

    const toAddress = outboundFields.to.trim();
    if (!toAddress) {
      datadogLogs.logger.error('Outbound email send skipped: recipient address is empty', { channel: 'email' });
      return false;
    }

    const cc = outboundFields.cc
      ?.split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(emailAddress => ({ emailAddress }));

    const sendEmailPayload: DraftEmailContact = {
      contactId,
      to: [{ emailAddress: toAddress }],
      cc: cc?.length ? cc : undefined,
      emailContent: {
        subject: outboundFields.subject,
        body: bodyHtml,
        bodyType: 'text/html' as const
      }
    };

    try {
      await client.sendEmail(sendEmailPayload);
    } catch (err) {
      datadogLogs.logger.error('Failed to send outbound email', { channel: 'email' }, err as Error);
      throw err;
    }

    let sentEmailData: Awaited<ReturnType<EmailClient['getEmailData']>> | undefined;
    let sentBodyHtml: string | undefined;
    let sentAttachments: EmailAttachment[] = [];

    try {
      sentEmailData = await client.getEmailData({ contactId, activeContactId: contactId });
      if (sentEmailData.bodyLocation) {
        sentBodyHtml = await fetchEmailBody(sentEmailData.bodyLocation, contactId);
      }
      sentAttachments = await resolveEmailAttachments(sentEmailData.attachmentLocations, contactId);
    } catch (fetchError) {
      datadogLogs.logger.error('Failed to fetch sent outbound email body', { channel: 'email' }, fetchError as Error);
    }

    const sentMessage: EmailMessage = {
      contactId,
      subject: sentEmailData?.subject ?? outboundFields.subject,
      from: sentEmailData?.from,
      to: sentEmailData?.to ?? [{ emailAddress: toAddress }],
      cc: sentEmailData?.cc ?? [],
      bodyHtml: sentBodyHtml,
      attachments: sentAttachments,
      timestamp: Date.now(),
      direction: 'OUTBOUND'
    };

    appendMessageToThread(contactId, sentMessage);
    return true;
  };

  const handleSendReply = async (contactId: string, bodyHtml: string): Promise<boolean> => {
    const data = emailData[contactId];

    if (!data || !data.to?.[0] || !data.from) {
      datadogLogs.logger.error('Reply email send skipped: contact data is incomplete', { channel: 'email' });
      return false;
    }

    const client = emailClientRef.current;
    const draftContactId = data.replyDraftContactId;

    if (client && draftContactId) {
      const sendEmailPayload: SendEmailRequest = {
        agentUserId: agentUserIdRef.current,
        from: { address: data.to[0] },
        to: { address: data.from, displayName: data.customerName },
        cc: data.cc?.map(ccAddress => ({ address: ccAddress })),
        subject: data.subject ?? 'No subject',
        bodyHtml,
        inReplyToContactId: contactId,
        idempotencyKey: crypto.randomUUID(),
        contactFlowId: config.emailContactFlowId || undefined,
        draftContactId
      };

      try {
        await sendEmail(sendEmailPayload);
      } catch (err) {
        datadogLogs.logger.error('Failed to send reply email', { channel: 'email' }, err as Error);
        throw err;
      }

      let sentEmailData: Awaited<ReturnType<EmailClient['getEmailData']>> | undefined;
      let sentBodyHtml: string | undefined;
      let sentAttachments: EmailAttachment[] = [];

      try {
        sentEmailData = await client.getEmailData({ contactId: draftContactId, activeContactId: contactId });
        if (sentEmailData.bodyLocation) {
          sentBodyHtml = await fetchEmailBody(sentEmailData.bodyLocation, draftContactId);
        }
        sentAttachments = await resolveEmailAttachments(sentEmailData.attachmentLocations, draftContactId);
      } catch (fetchError) {
        datadogLogs.logger.error('Failed to fetch sent reply email body', { channel: 'email' }, fetchError as Error);
      }

      const sentMessage: EmailMessage = {
        contactId: draftContactId,
        subject: sentEmailData?.subject ?? data.subject,
        from: sentEmailData?.from,
        to: sentEmailData?.deliveredTo ? [sentEmailData.deliveredTo] : (sentEmailData?.to ?? []),
        cc: sentEmailData?.cc ?? [],
        bodyHtml: sentBodyHtml,
        attachments: sentAttachments,
        timestamp: Date.now(),
        direction: sentEmailData?.direction ?? 'OUTBOUND'
      };

      appendMessageToThread(contactId, sentMessage);
      return true;
    }

    const payload: SendEmailRequest = {
      agentUserId: agentUserIdRef.current,
      from: { address: data.to[0] },
      to: { address: data.from, displayName: data.customerName },
      cc: data.cc?.map(ccAddress => ({ address: ccAddress })),
      subject: data.subject ?? 'No subject',
      bodyHtml,
      inReplyToContactId: contactId,
      idempotencyKey: crypto.randomUUID(),
      contactFlowId: config.emailContactFlowId || undefined
    };

    const { contactId: newContactId } = await sendEmail(payload);

    let sentEmailData: Awaited<ReturnType<EmailClient['getEmailData']>> | undefined;
    let sentBodyHtml: string | undefined;
    let sentAttachments: EmailAttachment[] = [];

    if (client) {
      try {
        sentEmailData = await client.getEmailData({ contactId: newContactId, activeContactId: contactId });
        if (sentEmailData.bodyLocation) {
          sentBodyHtml = await fetchEmailBody(sentEmailData.bodyLocation, newContactId);
        }
        sentAttachments = await resolveEmailAttachments(sentEmailData.attachmentLocations, newContactId);
      } catch (fetchError) {
        datadogLogs.logger.error('Failed to fetch sent reply email body', { channel: 'email' }, fetchError as Error);
      }
    }

    const sentMessage: EmailMessage = {
      contactId: newContactId,
      subject: sentEmailData?.subject ?? data.subject,
      from: sentEmailData?.from,
      to: sentEmailData?.deliveredTo ? [sentEmailData.deliveredTo] : (sentEmailData?.to ?? []),
      cc: sentEmailData?.cc ?? [],
      bodyHtml: sentBodyHtml,
      attachments: sentAttachments,
      timestamp: Date.now(),
      direction: sentEmailData?.direction ?? 'OUTBOUND'
    };

    appendMessageToThread(contactId, sentMessage);
    return true;
  };

  return { handleSend, isLoading };
}
